import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Target, UserPlus } from 'lucide-react';

const EventsPanel = ({ 
  game, 
  localTime,
  matchEvents, 
  onAddEvent, 
  onDeleteEvent 
}) => {
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [newEvent, setNewEvent] = useState({
    event_type: 'goal',
    team: 1,
    player_name: '',
    player_number: '',
    minute: Math.floor(localTime / 60),
    second_player_name: '',
    second_player_number: ''
  });

  const handleAddEvent = async () => {
    if (!newEvent.player_name.trim()) {
      toast.error('Player name is required');
      return;
    }
    
    await onAddEvent({
      ...newEvent,
      player_number: newEvent.player_number ? parseInt(newEvent.player_number) : null,
      second_player_number: newEvent.second_player_number ? parseInt(newEvent.second_player_number) : null
    });
    
    setShowEventDialog(false);
    setNewEvent({
      event_type: 'goal',
      team: 1,
      player_name: '',
      player_number: '',
      minute: Math.floor(localTime / 60),
      second_player_name: '',
      second_player_number: ''
    });
  };

  return (
    <div className="space-y-4">
      <Card className="bg-[#1E293B] border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-white font-body flex items-center justify-between">
            <span>Match Events</span>
            <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[#22C55E] hover:bg-[#16A34A]">
                  <Plus className="w-4 h-4 mr-1" /> Add Event
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#1E293B] border-slate-700 text-white">
                <DialogHeader>
                  <DialogTitle className="font-heading">Record Match Event</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300 font-body text-sm">Event Type</Label>
                      <Select 
                        value={newEvent.event_type} 
                        onValueChange={(v) => setNewEvent({...newEvent, event_type: v})}
                      >
                        <SelectTrigger className="bg-[#0F172A] border-slate-600 text-white mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1E293B] border-slate-600">
                          <SelectItem value="goal">⚽ Goal</SelectItem>
                          <SelectItem value="yellow_card">🟨 Yellow Card</SelectItem>
                          <SelectItem value="red_card">🟥 Red Card</SelectItem>
                          <SelectItem value="substitution">🔄 Substitution</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-300 font-body text-sm">Team</Label>
                      <Select 
                        value={String(newEvent.team)} 
                        onValueChange={(v) => setNewEvent({...newEvent, team: parseInt(v)})}
                      >
                        <SelectTrigger className="bg-[#0F172A] border-slate-600 text-white mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1E293B] border-slate-600">
                          <SelectItem value="1">{game?.team1?.name || 'Team 1'}</SelectItem>
                          <SelectItem value="2">{game?.team2?.name || 'Team 2'}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2">
                      <Label className="text-slate-300 font-body text-sm">Player Name</Label>
                      <Input
                        value={newEvent.player_name}
                        onChange={(e) => setNewEvent({...newEvent, player_name: e.target.value})}
                        className="bg-[#0F172A] border-slate-600 text-white font-body mt-1"
                        placeholder="Player name"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300 font-body text-sm">#</Label>
                      <Input
                        type="number"
                        value={newEvent.player_number}
                        onChange={(e) => setNewEvent({...newEvent, player_number: e.target.value})}
                        className="bg-[#0F172A] border-slate-600 text-white font-body mt-1"
                        placeholder="No."
                      />
                    </div>
                  </div>
                  
                  {newEvent.event_type === 'substitution' && (
                    <div className="grid grid-cols-3 gap-4 border-t border-slate-600 pt-4">
                      <div className="col-span-2">
                        <Label className="text-slate-300 font-body text-sm">Player Off</Label>
                        <Input
                          value={newEvent.second_player_name}
                          onChange={(e) => setNewEvent({...newEvent, second_player_name: e.target.value})}
                          className="bg-[#0F172A] border-slate-600 text-white font-body mt-1"
                          placeholder="Player coming off"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300 font-body text-sm">#</Label>
                        <Input
                          type="number"
                          value={newEvent.second_player_number}
                          onChange={(e) => setNewEvent({...newEvent, second_player_number: e.target.value})}
                          className="bg-[#0F172A] border-slate-600 text-white font-body mt-1"
                          placeholder="No."
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="w-24">
                    <Label className="text-slate-300 font-body text-sm">Minute</Label>
                    <Input
                      type="number"
                      value={newEvent.minute}
                      onChange={(e) => setNewEvent({...newEvent, minute: parseInt(e.target.value) || 0})}
                      className="bg-[#0F172A] border-slate-600 text-white font-body mt-1"
                    />
                  </div>
                  
                  <Button onClick={handleAddEvent} className="w-full bg-[#22C55E] hover:bg-[#16A34A] font-body">
                    Record Event
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {matchEvents.length === 0 ? (
            <p className="text-slate-500 font-body text-sm text-center py-4">
              No events recorded yet. Add goals, cards, or substitutions.
            </p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {matchEvents.sort((a, b) => a.minute - b.minute).map((event) => (
                <div key={event.id} className="flex items-center justify-between bg-[#0F172A] rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-lg text-slate-400 w-10">{event.minute}'</span>
                    <span className="text-lg">
                      {event.event_type === 'goal' && '⚽'}
                      {event.event_type === 'yellow_card' && '🟨'}
                      {event.event_type === 'red_card' && '🟥'}
                      {event.event_type === 'substitution' && '🔄'}
                    </span>
                    <div>
                      <p className="text-white font-body">
                        {event.player_number && `#${event.player_number} `}
                        {event.player_name}
                        {event.event_type === 'substitution' && event.second_player_name && (
                          <span className="text-slate-400"> ↔ {event.second_player_number && `#${event.second_player_number} `}{event.second_player_name}</span>
                        )}
                      </p>
                      <p className="text-slate-500 text-xs font-body">
                        {event.team === 1 ? game?.team1?.name : game?.team2?.name}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeleteEvent(event.id)}
                    className="text-slate-400 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      <p className="text-slate-500 text-xs text-center font-body">
        Events appear in the bottom score overlay with goalscorers
      </p>
    </div>
  );
};

export default EventsPanel;
